import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Button from "../../components/Button/Button3";
import Seo from "../../components/Seo/Seo";
import { ROUTES } from "../../utils/appRoutes";
import {
  buildAuthPathWithNext,
  getPostAuthFallbackPath,
  getSafeNextFromLocation,
} from "../../utils/authRedirect";
import "./ConfirmAccount.css";

function ConfirmAccount({ onLogin }) {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = getSafeNextFromLocation(location, getPostAuthFallbackPath(location));
  const loginPath = buildAuthPathWithNext(ROUTES.LOGIN, nextPath);

  useEffect(() => {
    if (location.state) {
      setEmail(location.state.email || "");
      setPassword(location.state.password || "");
    }
  }, [location]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const extractTokens = (data) => {
    if (!data) return null;

    // Preferred: data.tokens.{IdToken, AccessToken, RefreshToken}
    if (data.tokens?.IdToken && data.tokens?.AccessToken) {
      return {
        IdToken: data.tokens.IdToken,
        AccessToken: data.tokens.AccessToken,
        RefreshToken: data.tokens.RefreshToken || null,
      };
    }

    // Preferred flat: { IdToken, AccessToken, RefreshToken }
    if (data.IdToken && data.AccessToken) {
      return {
        IdToken: data.IdToken,
        AccessToken: data.AccessToken,
        RefreshToken: data.RefreshToken || null,
      };
    }

    // Support camelCase: { tokens: { idToken, accessToken, refreshToken } }
    if (data.tokens?.idToken && data.tokens?.accessToken) {
      return {
        IdToken: data.tokens.idToken,
        AccessToken: data.tokens.accessToken,
        RefreshToken: data.tokens.refreshToken || null,
      };
    }

    // Support camelCase flat: { idToken, accessToken, refreshToken }
    if (data.idToken && data.accessToken) {
      return {
        IdToken: data.idToken,
        AccessToken: data.accessToken,
        RefreshToken: data.refreshToken || null,
      };
    }

    return null;
  };

  const handleConfirm = async () => {
    if (!confirmationCode || !email) {
      setMessage("Please provide email and confirmation code.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/confirm`,
        { email, confirmationCode, password }
      );

      const tokens = extractTokens(response.data);

      if (tokens?.IdToken && tokens?.AccessToken) {
        setMessage("Account confirmed and logged in successfully!");
        onLogin(tokens.IdToken, tokens.AccessToken, tokens.RefreshToken);
        navigate(nextPath, { replace: true });
        return;
      }

      // Confirmed but no tokens
      const confirmed =
        response.data?.confirmed === true ||
        (typeof response.data?.message === "string" &&
          response.data.message.toLowerCase().includes("confirmed"));

      if (confirmed) {
        setMessage("Account confirmed successfully. Please log in.");
        navigate(loginPath, { state: { email, next: nextPath } });
        return;
      }

      setMessage("Account confirmed, but no tokens were returned. Please log in.");
      navigate(loginPath, { state: { email, next: nextPath } });
    } catch (error) {
      const confirmed = error.response?.data?.confirmed === true;
      if (confirmed) {
        setMessage(
          error.response?.data?.error ||
            "Account confirmed, but auto-login failed. Please log in."
        );
        navigate(loginPath, { state: { email, next: nextPath } });
        return;
      }

      setMessage(error.response?.data?.error || "Account confirmation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setMessage("Email is missing. Cannot resend code.");
      return;
    }

    setResendDisabled(true);
    setTimer(30);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/resend-confirmation-code`,
        { email }
      );
      setMessage(
        response.data.message ||
          "A new code has been sent to your email. Please check your inbox and spam folder."
      );
    } catch (error) {
      setMessage(error.response?.data?.error || "Could not resend code.");
      setResendDisabled(false);
      setTimer(0);
    }
  };

  return (
    <>
      <Seo
        title="Confirm Account | ZealFC"
        description="Confirm your ZealFC account to complete registration and access football games."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.CONFIRM}
      />
      <div className="confirm-container">
        <div className="confirm-box">
          <h2>Confirm Your Account</h2>
          <p className="instruction-text">
            A confirmation code has been sent to your email:
            <strong> {email || "your email address"} </strong>. Please check your inbox and spam folder, then enter it below.
          </p>

          <input
            type="text"
            placeholder="Enter confirmation code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            className="input-field"
          />

          {!password && (
            <input
              type="password"
              placeholder="Enter your password (optional for auto-login)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
            />
          )}

          <Button
            variant="small"
            primaryText={loading ? "..." : "Confirm"}
            onClick={handleConfirm}
            styleType="default"
          />

          <div className="resend-container">
            <p className="resend-text">
              {resendDisabled ? (
                `Resend again in ${timer} seconds`
              ) : (
                <span className="resend-link" onClick={handleResendCode}>
                  Resend Code
                </span>
              )}
            </p>
          </div>

          <p className="message">{message}</p>
        </div>
      </div>
    </>
  );
}

export default ConfirmAccount;
